import { inject, Injectable } from '@angular/core'
import { firstValueFrom } from 'rxjs'
import { Entries, EntriesService, GoalsService, ParticipantGoal } from 'superlikers-microsites-api'

import { Badge, BadgeDisplayType } from '../../models'

/**
 * Visual config stored on the goal, e.g. { color: 'green', background_color: ['#3544EC', ...], category: 'clicks' }.
 * `category` identifies the external entry that marks this badge as already shown to the participant.
 * When it isn't configured on the goal, the goal name is used as fallback (names are unique per goal).
 */
interface GoalMetadata {
  color?: string
  background_color?: string[]
  category?: string
  tiro_libre?: string
}

const BADGE_TAG_DISPLAY_TYPE: Record<string, BadgeDisplayType> = {
  progress: 'progress'
}

@Injectable({
  providedIn: 'root'
})
export class SalaDeTrofeosApi {
  #goalsService = inject(GoalsService)
  #entriesService = inject(EntriesService)

  async getBadgeGoals(): Promise<Badge[]> {
    const response = await firstValueFrom(this.#goalsService.getGoals({ page: 1, tags: ['badges'] }))
    return (response.data as ParticipantGoal<GoalMetadata>[]).map(goal => this.badgeAdapter(goal))
  }

  async getSeenBadgeEntries(): Promise<Entries<unknown>[]> {
    const response = await firstValueFrom(this.#entriesService.getEntries({ _type: 'External', atype: 'badges', limit: 100 }))
    return response.data
  }

  async markBadgeAsSeen(category: string): Promise<void> {
    await firstValueFrom(this.#entriesService.sendExternal({ event: 'badges', category, properties: { date: new Date() } }))
  }

  /**
   * Transform a participant goal into a Badge, resolving its display variant from tags.
   */
  private badgeAdapter(goal: ParticipantGoal<GoalMetadata>): Badge {
    console.log(goal)
    const displayType = this.resolveDisplayType(goal.tags)
    const achieved = goal.status === '100%'

    const core = {
      id: goal.goal_id,
      name: goal.name,
      description: goal.desc,
      image: goal.image?.url ?? '',
      category: goal.goal_data?.category || goal.name,
      color: goal.goal_data?.color ?? '',
      colors: goal.goal_data?.background_color ?? [],
      tiroLibre: goal.goal_data?.tiro_libre ?? null,
      achieved
    }

    if (displayType === 'progress') {
      const current = goal.winning_dates.length
      const total = goal.interval_samples
      return { ...core, display: { type: 'progress', current, total } }
    }

    return { ...core, display: { type: 'simple' } }
  }

  private resolveDisplayType(tags: string[]): BadgeDisplayType {
    const tag = tags.find(t => t in BADGE_TAG_DISPLAY_TYPE)
    return tag ? BADGE_TAG_DISPLAY_TYPE[tag] : 'simple'
  }
}
